import { Injectable, computed, signal } from '@angular/core';
import { AccessTokenPayload, decodeAccessToken } from '../models/access-token.model';
import { environment } from '../../../environments/environment';

const STORAGE_KEYS = {
  token: 'token',
  language: 'language',
} as const;

export const ACCESS_TOKEN_REFRESH_MARGIN_SECONDS = 60;

@Injectable({ providedIn: 'root' })
export class SessionStore {
  readonly accessToken = signal<string | null>(localStorage.getItem(STORAGE_KEYS.token));
  readonly language = signal<string>(localStorage.getItem(STORAGE_KEYS.language) ?? environment.defaultLanguage);

  readonly tokenPayload = computed<AccessTokenPayload | null>(() => decodeAccessToken(this.accessToken()));
  readonly isOtpRequired = computed(() => !!this.tokenPayload()?.otp_confirmation_required);
  readonly isResetPasswordRequired = computed(() => !!this.tokenPayload()?.reset_password_required);

  constructor() {
    window.addEventListener('storage', event => {
      if (event.key === null || event.key === STORAGE_KEYS.token) {
        this.accessToken.set(localStorage.getItem(STORAGE_KEYS.token));
      }
    });
  }

  isAuthenticated(): boolean {
    const payload = this.tokenPayload();
    if (!payload) {
      return false;
    }
    if (!payload.refresh_token_expiration) {
      return true;
    }
    return payload.refresh_token_expiration > this.nowSeconds();
  }

  isAccessTokenExpiring(marginSeconds = ACCESS_TOKEN_REFRESH_MARGIN_SECONDS): boolean {
    const payload = this.tokenPayload();
    return !!payload && payload.exp - marginSeconds <= this.nowSeconds();
  }

  isRefreshTokenExpired(): boolean {
    const payload = this.tokenPayload();
    if (!payload) {
      return true;
    }
    if (!payload.refresh_token_expiration) {
      return false;
    }
    return payload.refresh_token_expiration <= this.nowSeconds();
  }

  setAccessToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.token, token);
    this.accessToken.set(token);
  }

  setLanguage(language: string): void {
    localStorage.setItem(STORAGE_KEYS.language, language);
    this.language.set(language);
  }

  clear(): void {
    const language = this.language();
    localStorage.clear();
    localStorage.setItem(STORAGE_KEYS.language, language);
    this.accessToken.set(null);
  }

  private nowSeconds(): number {
    return Date.now() / 1000;
  }
}
